import React, { useRef, useState } from "react";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { HindiInput } from "../HindiInput";
import { GharDialog } from "../GharDialog";
import { TimingCard } from "../TimingCard";
import {
  AREA_INFO, GHARS, PATHIS, PREACHERS, GHAR_TYPE_HI, WEEKDAYS_HI, ACTIVE_AREA, isDefaultArea,
  saveAreaInfo, saveGhars, savePeople, deleteArea,
} from "../../data/rssbData";
import { hinglishToHindi } from "../../lib/translit";
import { downloadAreaTemplate, parseAreaFile } from "../../lib/areaImport";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, UploadCloud, Download, Save } from "lucide-react";

const card = "bg-[#fffdf7] border border-[#e3d6b6] rounded-xl p-4 space-y-3";
const inCls = "mt-1 bg-[#f6eeda] border-[#e3d6b6] text-[#3a1212] font-dev";
const FIELDS = [
  ["areaHi", "क्षेत्र का नाम"],
  ["secretaryNameHi", "सेक्रेटरी का नाम (PDF में ऊपर दाईं ओर)"],
  ["secretaryDesignationHi", "सेक्रेटरी पद"],
  ["phones", "सेक्रेटरी मोबाइल (जैसे मोः 9760112220; 8979822222)"],
  ["leavePhones", "छुट्टी सूचना मोबाइल (PDF नोट 2)"],
  ["infoContactHi", "अधिक जानकारी हेतु संपर्क (PDF नोट 3)"],
];
const RAW = ["phones", "leavePhones"];

const AreaInfoCard = () => {
  const [f, setF] = useState(() => Object.fromEntries(FIELDS.map(([k]) => [k, AREA_INFO[k] || ""])));
  const save = async () => {
    const next = {};
    for (const [k] of FIELDS) next[k] = RAW.includes(k) ? f[k].trim() : (await hinglishToHindi(f[k].trim())).trim();
    saveAreaInfo(next);
    setF(next);
    toast.success("क्षेत्र विवरण सहेजा गया");
  };
  return (
    <div className={card} data-testid="area-info-card">
      <h3 className="font-serif-dev font-semibold text-[#3a1212]">क्षेत्र विवरण</h3>
      {FIELDS.map(([k, label]) => (
        <div key={k}>
          <Label className="text-xs text-[#5a2a24]">{label}</Label>
          <HindiInput testid={`area-${k}`} value={f[k]} onChange={(v) => setF((s) => ({ ...s, [k]: v }))} className={inCls} />
        </div>
      ))}
      <Button data-testid="area-info-save" onClick={save} className="bg-[#6e1b1b] text-[#f3ead6] hover:bg-[#7a1f1f] gap-1">
        <Save className="w-4 h-4" /> सहेजें
      </Button>
    </div>
  );
};

const GharManager = () => {
  const [, setVer] = useState(0);
  const [dlg, setDlg] = useState({ open: false, ghar: null });
  const commit = (list, msg) => {
    saveGhars(list);
    setVer((v) => v + 1);
    toast.success(msg);
  };
  const onSave = (g) => {
    const exists = GHARS.some((x) => x.id === g.id);
    commit(exists ? GHARS.map((x) => (x.id === g.id ? g : x)) : [...GHARS, g], exists ? "सत्संग घर अपडेट हुआ" : "सत्संग घर जोड़ा गया");
    setDlg({ open: false, ghar: null });
  };
  const onDelete = (g) => window.confirm(`${g.hi} हटाएं?`) && commit(GHARS.filter((x) => x.id !== g.id), "सत्संग घर हटाया गया");
  return (
    <div className={card} data-testid="ghar-manager">
      <div className="flex items-center justify-between gap-2">
        <h3 className="font-serif-dev font-semibold text-[#3a1212]">सत्संग घर ({GHARS.length})</h3>
        <Button data-testid="ghar-add" size="sm" onClick={() => setDlg({ open: true, ghar: null })} className="bg-[#6e1b1b] text-[#f3ead6] hover:bg-[#7a1f1f] gap-1">
          <Plus className="w-4 h-4" /> नया घर
        </Button>
      </div>
      <div className="space-y-1.5 max-h-[420px] overflow-y-auto pr-1">
        {GHARS.map((g) => (
          <div key={g.id} data-testid={`ghar-row-${g.id}`} className="flex items-center justify-between gap-2 bg-[#f6eeda] border border-[#e3d6b6] rounded-lg px-3 py-2">
            <div className="min-w-0 font-dev text-sm text-[#3a1212]">
              {g.hi}<span className="text-[#6e1b1b]">–{GHAR_TYPE_HI[g.type]}</span>
              <span className="block text-[11px] text-[#9a7a5a]">{g.days.map((d) => WEEKDAYS_HI[d]).join(", ")}</span>
            </div>
            <div className="flex shrink-0">
              <button data-testid={`ghar-edit-${g.id}`} onClick={() => setDlg({ open: true, ghar: g })} className="p-1.5 text-[#6e1b1b]"><Pencil className="w-3.5 h-3.5" /></button>
              <button data-testid={`ghar-delete-${g.id}`} onClick={() => onDelete(g)} className="p-1.5 text-[#b91c1c]"><Trash2 className="w-3.5 h-3.5" /></button>
            </div>
          </div>
        ))}
        {!GHARS.length && <p className="text-sm text-[#9a7a5a] font-dev py-4 text-center">अभी कोई सत्संग घर नहीं — जोड़ें या Excel अपलोड करें</p>}
      </div>
      <GharDialog open={dlg.open} ghar={dlg.ghar} onClose={() => setDlg({ open: false, ghar: null })} onSave={onSave} />
    </div>
  );
};

const merge = (oldList, newList) => [...oldList, ...newList.filter((n) => !oldList.some((o) => o.hi === n.hi))];

const ExcelImportCard = () => {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const r = await parseAreaFile(file);
      saveGhars(merge(GHARS, r.ghars));
      savePeople("pathi", merge(PATHIS, r.pathis));
      savePeople("preacher", merge(PREACHERS, r.preachers));
      toast.success(`Excel से जोड़े गए — घर: ${r.ghars.length}, पाठी: ${r.pathis.length}, SR/SK: ${r.preachers.length}`);
      setTimeout(() => window.location.reload(), 1200);
    } catch (err) {
      toast.error("Excel पढ़ने में त्रुटि: " + err.message);
    } finally {
      setBusy(false);
      if (ref.current) ref.current.value = "";
    }
  };
  return (
    <div className={card} data-testid="area-excel-card">
      <h3 className="font-serif-dev font-semibold text-[#3a1212]">Excel से पूरा डेटा भरें</h3>
      <p className="text-xs text-[#7a4a3a] font-dev">
        टेम्पलेट में 3 शीट हैं: Satsang Ghar, Pathi, SR-SK। नाम हिंग्लिश में भी लिख सकते हैं — अपने आप हिंदी बन जाएंगे। पहले से मौजूद नाम दोबारा नहीं जुड़ेंगे; बाद में हर चीज़ बदल/हटा सकते हैं।
      </p>
      <div className="flex flex-wrap gap-2">
        <Button data-testid="area-template-download" variant="outline" onClick={downloadAreaTemplate} className="border-[#6e1b1b]/40 text-[#5e1414] gap-1">
          <Download className="w-4 h-4" /> टेम्पलेट डाउनलोड
        </Button>
        <Button data-testid="area-excel-upload" disabled={busy} onClick={() => ref.current?.click()} className="bg-[#6e1b1b] text-[#f3ead6] hover:bg-[#7a1f1f] gap-1">
          <UploadCloud className="w-4 h-4" /> {busy ? "पढ़ा जा रहा है..." : "Excel अपलोड"}
        </Button>
        <input ref={ref} type="file" accept=".xlsx,.xls,.csv" className="hidden" onChange={onFile} data-testid="area-excel-input" />
      </div>
    </div>
  );
};

export const AreaSetupTab = () => (
  <div className="space-y-4">
    <div className="grid gap-4 lg:grid-cols-2">
      <div className="space-y-4">
        <AreaInfoCard />
        <TimingCard />
        <ExcelImportCard />
      </div>
      <GharManager />
    </div>
    <p className="text-xs text-[#7a4a3a] font-dev">पाठी व SR/SK जोड़ने/बदलने के लिए "पाठी व SR सूची" टैब देखें।</p>
    {!isDefaultArea && (
      <Button
        data-testid="area-delete"
        variant="destructive"
        onClick={() => window.confirm(`${AREA_INFO.areaHi} क्षेत्र और इसका पूरा डेटा हटाएं?`) && deleteArea(ACTIVE_AREA)}
        className="gap-1"
      >
        <Trash2 className="w-4 h-4" /> यह क्षेत्र हटाएं
      </Button>
    )}
  </div>
);
